import type { Input, InputState } from './types';

export function createInputState(): InputState {
  return {
    moveX: 0,
    moveY: 0,
    lookDX: 0,
    lookDY: 0,
    sprint: false,
    fire: false,
    aim: false,
    handbrake: false,
    horn: false,
    jump: false,
    interact: false,
    nextWeapon: false,
    prevWeapon: false,
    weaponSlot: 0,
    radioNext: false,
    pause: false,
    cameraToggle: false,
    touch: false,
    pointerLocked: false,
  };
}

/**
 * Keyboard + mouse input. Touch controls (touch.ts) write into the same state
 * and set `state.touch = true`; when touch is active, keyboard axes are ignored.
 */
export function createInput(canvas: HTMLCanvasElement): Input {
  const state = createInputState();
  const keys = new Set<string>();
  const pressed = new Set<string>(); // edge flags for this frame

  const isTyping = () => {
    const el = document.activeElement as HTMLElement | null;
    return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
  };

  window.addEventListener('keydown', (e) => {
    if (isTyping()) return;
    const k = e.code;
    if (!keys.has(k)) pressed.add(k);
    keys.add(k);
    if (['Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(k)) e.preventDefault();
  });
  window.addEventListener('keyup', (e) => keys.delete(e.code));
  window.addEventListener('blur', () => {
    keys.clear();
    state.fire = state.aim = false;
  });

  let mouseDown = false;
  let rmbDown = false;
  canvas.addEventListener('mousedown', (e) => {
    if (e.button === 0) mouseDown = true;
    if (e.button === 2) rmbDown = true;
  });
  window.addEventListener('mouseup', (e) => {
    if (e.button === 0) mouseDown = false;
    if (e.button === 2) rmbDown = false;
  });
  canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  window.addEventListener('mousemove', (e) => {
    if (document.pointerLockElement === canvas) {
      state.lookDX += e.movementX;
      state.lookDY += e.movementY;
    }
  });
  let wheelAccum = 0;
  canvas.addEventListener(
    'wheel',
    (e) => {
      e.preventDefault();
      wheelAccum += e.deltaY;
      if (wheelAccum > 40) {
        pressed.add('WheelDown');
        wheelAccum = 0;
      } else if (wheelAccum < -40) {
        pressed.add('WheelUp');
        wheelAccum = 0;
      }
    },
    { passive: false },
  );
  document.addEventListener('pointerlockchange', () => {
    state.pointerLocked = document.pointerLockElement === canvas;
  });

  const axis = (neg: string[], pos: string[]) => (neg.some((k) => keys.has(k)) ? -1 : 0) + (pos.some((k) => keys.has(k)) ? 1 : 0);

  /** Recompute held state from the key set. Called at the start of each frame by `poll()`. */
  function poll() {
    if (!state.touch) {
      state.moveX = axis(['KeyA', 'ArrowLeft', 'KeyQ'], ['KeyD', 'ArrowRight']);
      state.moveY = axis(['KeyS', 'ArrowDown'], ['KeyW', 'ArrowUp', 'KeyZ']);
      state.sprint = keys.has('ShiftLeft') || keys.has('ShiftRight');
      state.fire = mouseDown || keys.has('ControlLeft');
      state.aim = rmbDown;
      state.handbrake = keys.has('Space');
      state.horn = keys.has('KeyH');
    }
    state.jump = pressed.has('Space');
    state.interact = pressed.has('KeyE') || pressed.has('KeyF') || pressed.has('Enter');
    state.nextWeapon = pressed.has('WheelDown') || pressed.has('BracketRight');
    state.prevWeapon = pressed.has('WheelUp') || pressed.has('BracketLeft');
    state.weaponSlot = 0;
    for (let i = 1; i <= 9; i++) if (pressed.has('Digit' + i)) state.weaponSlot = i;
    state.radioNext = pressed.has('KeyR');
    state.pause = pressed.has('Escape') || pressed.has('KeyP');
    state.cameraToggle = pressed.has('KeyV');
  }

  const input: Input & { poll: () => void } = {
    state,
    poll,
    endFrame() {
      pressed.clear();
      state.lookDX = 0;
      state.lookDY = 0;
      state.jump = state.interact = state.nextWeapon = state.prevWeapon = state.radioNext = state.pause = state.cameraToggle = false;
      state.weaponSlot = 0;
    },
    requestPointerLock() {
      if (document.pointerLockElement !== canvas) {
        try {
          const p = canvas.requestPointerLock({ unadjustedMovement: true } as unknown as undefined) as unknown as Promise<void> | undefined;
          if (p && typeof (p as Promise<void>).catch === 'function') (p as Promise<void>).catch(() => canvas.requestPointerLock());
        } catch {
          try {
            canvas.requestPointerLock();
          } catch {
            /* ignore */
          }
        }
      }
    },
    exitPointerLock() {
      if (document.pointerLockElement) document.exitPointerLock();
    },
  };
  return input;
}

export type PollableInput = Input & { poll: () => void };
